import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Card, Table, Button, Tag, Space, Typography, Upload, Modal, Input,
  Popconfirm, Empty, Tooltip,
} from "antd";
import {
  InboxOutlined, ReloadOutlined, AndroidOutlined, DownloadOutlined,
  StarOutlined, StarFilled, EditOutlined, DeleteOutlined, ExportOutlined,
} from "@ant-design/icons";
import {
  listApks, uploadApk, setApkLatest, updateApk, deleteApk,
} from "../network/api";
import { notify, serverError } from "../utils/notify";
import { useAuth } from "../context/AuthContext";
import { relativeTime } from "../constants/health";

const { Text, Paragraph } = Typography;
const { Dragger } = Upload;
const { TextArea } = Input;

const mb = (n) => (n ? `${(n / (1024 * 1024)).toFixed(1)} MB` : "—");

// The public download landing page lives at /download on this same host.
const PUBLIC_URL = `${window.location.origin}/download`;

// Admin view for Android app distribution: upload an APK (version is parsed from
// the manifest), pick which build the public /download page offers, edit release
// notes, and remove old builds.
export default function MobileApp() {
  const { isHardwareAdmin } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(null); // filename currently mutating
  const [editing, setEditing] = useState(null); // build whose notes are being edited
  const [notesDraft, setNotesDraft] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRows(await listApks());
    } catch (e) {
      notify.error(serverError(e, "Failed to load app builds"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const onUpload = async (file) => {
    setUploading(true);
    try {
      const doc = await uploadApk(file);
      notify.success(`Uploaded ${doc.versionName || doc.filename} — now the latest download.`);
      await load();
    } catch (e) {
      notify.error(serverError(e, "APK upload failed"));
    } finally {
      setUploading(false);
    }
    return false; // block antd's default upload
  };

  const act = async (filename, fn, okMsg) => {
    setBusy(filename);
    try {
      await fn();
      notify.success(okMsg);
      await load();
    } catch (e) {
      notify.error(serverError(e, "Action failed"));
    } finally {
      setBusy(null);
    }
  };

  const openEdit = (row) => {
    setEditing(row);
    setNotesDraft(row.notes || "");
  };
  const saveNotes = () =>
    act(editing.filename, () => updateApk(editing.filename, { notes: notesDraft }), "Release notes saved")
      .then(() => setEditing(null));

  const columns = useMemo(
    () => [
      {
        title: "Version",
        dataIndex: "versionName",
        render: (v, r) => (
          <Space direction="vertical" size={0}>
            <Space size={6}>
              <Text strong>{v || "—"}</Text>
              {r.versionCode != null && <Text type="secondary">(build {r.versionCode})</Text>}
              {r.isLatest && <Tag color="green">Latest</Tag>}
            </Space>
            <Text type="secondary" style={{ fontSize: 12 }}>{r.package || r.filename}</Text>
          </Space>
        ),
      },
      {
        title: "SDK",
        key: "sdk",
        render: (_, r) =>
          r.minSdkVersion || r.targetSdkVersion ? (
            <Text type="secondary" style={{ fontSize: 12 }}>
              min {r.minSdkVersion ?? "—"} → target {r.targetSdkVersion ?? "—"}
            </Text>
          ) : (
            <Text type="secondary">—</Text>
          ),
      },
      { title: "Size", dataIndex: "size", render: mb },
      { title: "Uploaded", dataIndex: "createdAt", render: (v) => (v ? relativeTime(v) : "—") },
      {
        title: "Notes",
        dataIndex: "notes",
        render: (n) =>
          n ? (
            <Tooltip title={n}>
              <Text style={{ fontSize: 12 }} ellipsis={{ tooltip: false }}>
                {n.length > 40 ? `${n.slice(0, 40)}…` : n}
              </Text>
            </Tooltip>
          ) : (
            <Text type="secondary">—</Text>
          ),
      },
      {
        title: "Actions",
        key: "actions",
        render: (_, r) => (
          <Space wrap>
            <Button size="small" icon={<DownloadOutlined />} href={r.url} target="_blank" rel="noreferrer">
              Download
            </Button>
            {isHardwareAdmin && (
              <>
                <Button
                  size="small"
                  icon={r.isLatest ? <StarFilled /> : <StarOutlined />}
                  disabled={r.isLatest}
                  loading={busy === r.filename}
                  onClick={() => act(r.filename, () => setApkLatest(r.filename), `${r.versionName || r.filename} is now the latest download`)}
                >
                  {r.isLatest ? "Latest" : "Set latest"}
                </Button>
                <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)}>
                  Notes
                </Button>
                <Popconfirm
                  title="Delete this build?"
                  description="The APK file and its record are removed. This cannot be undone."
                  okText="Delete"
                  okButtonProps={{ danger: true }}
                  onConfirm={() => act(r.filename, () => deleteApk(r.filename), "Build deleted")}
                >
                  <Button size="small" danger icon={<DeleteOutlined />} loading={busy === r.filename} />
                </Popconfirm>
              </>
            )}
          </Space>
        ),
      },
    ],
    [isHardwareAdmin, busy] // eslint-disable-line react-hooks/exhaustive-deps
  );

  return (
    <Card
      title={<Space><AndroidOutlined /> Mobile App (Android)</Space>}
      extra={
        <Space>
          <Button size="small" icon={<ExportOutlined />} href={PUBLIC_URL} target="_blank" rel="noreferrer">
            Open public page
          </Button>
          <Button size="small" icon={<ReloadOutlined />} onClick={load} loading={loading}>Refresh</Button>
        </Space>
      }
    >
      <Paragraph type="secondary">
        Upload an Android <b>.apk</b> — the version is read straight from the app manifest. The newest upload becomes the
        default download on the public page at <a href={PUBLIC_URL} target="_blank" rel="noreferrer">{PUBLIC_URL}</a>,
        which anyone can visit without signing in. Use <b>Set latest</b> to feature an older build instead.
      </Paragraph>

      {isHardwareAdmin && (
        <Dragger beforeUpload={onUpload} showUploadList={false} accept=".apk" disabled={uploading} style={{ marginBottom: 16 }}>
          <p className="ant-upload-drag-icon"><InboxOutlined /></p>
          <p className="ant-upload-text">{uploading ? "Uploading…" : "Drop an Android .apk here to upload"}</p>
          <p className="ant-upload-hint">Version name & code are extracted from the APK automatically.</p>
        </Dragger>
      )}

      <Table
        rowKey="filename"
        size="small"
        loading={loading}
        columns={columns}
        dataSource={rows}
        pagination={{ pageSize: 10, hideOnSinglePage: true }}
        locale={{ emptyText: <Empty description="No app builds uploaded yet" /> }}
      />

      <Modal
        open={!!editing}
        title={`Release notes — ${editing?.versionName || editing?.filename || ""}`}
        okText="Save"
        onOk={saveNotes}
        confirmLoading={busy === editing?.filename}
        onCancel={() => setEditing(null)}
      >
        <Paragraph type="secondary">Shown to users on the public download page.</Paragraph>
        <TextArea
          rows={6}
          value={notesDraft}
          onChange={(e) => setNotesDraft(e.target.value)}
          placeholder="What's new in this version…"
          maxLength={4000}
          showCount
        />
      </Modal>
    </Card>
  );
}
